import React, { useState } from 'react';

// --- IMPORTACIÓN DE NUESTRAS UTILIDADES DE SEGUROS ---
import { formatDateShort, formatCurrencyUniversal } from '../../../../utils/segurosUtils';

// --- ICONOS LOCALES PREMIUM ---
const IconGroup = () => (
    <svg className="w-4 h-4 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
);

const IconChevronDown = ({ className = "w-4 h-4 text-white" }) => (
    <svg className={`${className} transition-transform duration-200`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
);

const IconTrendUp = () => (
    <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v6m0-6L10 17l-4-4-6 6" />
    </svg>
);

const IconTrendDown = () => (
    <svg className="w-3.5 h-3.5 text-rose-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 17h8m0 0v-6m0 6L10 7l-4 4-6-6" />
    </svg>
);

const MONETARY_KEYS = [
    'importe', 'premio', 'primaAdelanto', 'primaEmitida', 'primaPrimeraCuota', 
    'importeReclamado', 'primaAnulacion', 'primaEmitidaRefacturacion', 'premioRefacturacion'
];

const BADGE_KEYS = [
    'estadoEmision', 'estado', 'estadoGestion', 'estadoRefacturacion', 'estadoGestionRenovacion',
    'auditoriaRenovacion', 'tieneDeuda', 'tieneSiniestro', 'pagoEfectuado'
];

const DATE_KEYS = [
    'fechaCarga', 'fechaCargaCobranza', 'fechaCargaSiniestro', 'fechaCargaEndoso', 
    'fechaCargaRefacturaciones', 'fechaAnulacion', 'inicioVigencia', 'finVigencia', 
    'fechaRefacturacion', 'inicioVigenciaRefac', 'finVigenciaRefac', 'fechaSiniestro', 
    'fechaCobro', 'fechaCobroRefacturacion', 'fechaReclamo', 'fechaReclamoRefacturacion', 
    'fechaDespacho'
];

const GroupedPreview = ({ registrosAgrupados, columnasSeleccionadas, camposDisponibles, moduloRaiz, agruparPor }) => {
    const [collapsedGroups, setCollapsedGroups] = useState({});

    const toggleGroup = (grupoNombre) => {
        setCollapsedGroups(prev => ({
            ...prev,
            [grupoNombre]: !prev[grupoNombre]
        }));
    };

    const formatCellValue = (key, value, row) => {
        if (value === undefined || value === null || value === '') return '-';

        // 1. Badges Semánticos de Estado
        if (BADGE_KEYS.includes(key)) {
            const stringVal = String(value).toUpperCase().trim();

            if (['tieneDeuda', 'tieneSiniestro'].includes(key)) {
                const isYes = value === true || stringVal === 'SÍ' || stringVal === 'SI';
                return isYes ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black bg-rose-50 text-rose-700 border border-rose-100">
                        <span className="w-1 h-1 rounded-full bg-rose-500 animate-pulse"></span>
                        SÍ
                    </span>
                ) : (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-slate-100 text-slate-500 border border-slate-200/50">
                        NO
                    </span>
                );
            }

            if (key === 'pagoEfectuado') {
                const isYes = value === true || stringVal === 'SÍ' || stringVal === 'SI';
                return isYes ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-black bg-emerald-50 text-emerald-700 border border-emerald-100">
                        SÍ
                    </span>
                ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-black bg-rose-50 text-rose-700 border border-rose-100">
                        NO
                    </span>
                );
            }

            const isGreen = ['VIGENTE', 'PAGADO', 'EMITIDO', 'ACTIVO', 'RENOVACIÓN EMITIDA', 'NORMAL', 'VIGENTE ACTIVA', 'SI', 'SÍ'].some(s => stringVal.includes(s));
            const isRed = ['VENCIDA', 'ANULADA', 'CANCELADA', 'RECHAZADA', 'CRÍTICO', 'NO GESTIONADA', 'ATRASADO', 'VENCIDA SIN REVISIÓN', 'PUNTO CIEGO'].some(s => stringVal.includes(s));
            const isYellow = ['PENDIENTE', 'EN PROCESO', 'NO GESTIONADA', 'NO INICIADA', 'RECLAMADO', 'REFACTURACION', 'REFACTURACIÓN'].some(s => stringVal.includes(s));

            if (isGreen) {
                return (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-black bg-emerald-50 text-emerald-700 border border-emerald-100">
                        {stringVal}
                    </span>
                );
            } else if (isRed) {
                return (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-black bg-rose-50 text-rose-700 border border-rose-150">
                        {stringVal}
                    </span>
                );
            } else if (isYellow) {
                return (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-black bg-amber-50 text-amber-800 border border-amber-150">
                        {stringVal}
                    </span>
                );
            }

            return (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold bg-slate-100 text-slate-600 rounded-md">
                    {stringVal}
                </span>
            );
        }

        if (MONETARY_KEYS.includes(key)) {
            return formatCurrencyUniversal(value, row.moneda || '$');
        }

        return String(value);
    };

    return (
        <div className="space-y-4">
            {registrosAgrupados.map(grupo => {
                const isCollapsed = collapsedGroups[grupo.nombre] || false;
                return (
                    <div 
                        key={grupo.nombre} 
                        className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden text-left flex flex-col"
                    >
                        <div 
                            onClick={() => toggleGroup(grupo.nombre)}
                            className="p-4 bg-slate-50 border-b border-slate-100 flex items-center justify-between cursor-pointer hover:bg-slate-100/50 transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <span className="bg-indigo-50 border border-indigo-100 text-indigo-700 px-2 py-0.5 rounded-md text-[10px] font-bold">
                                    {grupo.items.length} {grupo.items.length === 1 ? 'caso' : 'casos'}
                                </span>
                                <span className="font-bold text-slate-800 uppercase text-xs truncate max-w-[400px]">
                                    {agruparPor}: {grupo.nombre}
                                </span>
                            </div>
                            <div className="flex items-center gap-6">
                                {mostrarAcumulados(grupo) && (
                                    <div className="flex items-center gap-4 text-[10px] font-bold uppercase text-slate-400 select-none">
                                        {grupo.totalPrima > 0 && (
                                            <span className="flex items-center gap-1.5">
                                                <IconTrendUp />
                                                <span>Prima:</span>
                                                <span className="text-emerald-600 font-mono font-black text-xs">
                                                    {formatCurrencyUniversal(grupo.totalPrima, '$')}
                                                </span>
                                            </span>
                                        )}
                                        {grupo.totalImporte > 0 && (
                                            <span className="flex items-center gap-1.5">
                                                <IconTrendDown />
                                                <span>Deuda:</span>
                                                <span className="text-rose-600 font-mono font-black text-xs">
                                                    {formatCurrencyUniversal(grupo.totalImporte, '$')}
                                                </span>
                                            </span>
                                        )}
                                    </div>
                                )}
                                <IconChevronDown className={`w-3.5 h-3.5 text-slate-400 transform transition-transform ${isCollapsed ? '' : 'rotate-180'}`} />
                            </div>
                        </div>

                        {!isCollapsed && (
                            <div className="overflow-x-auto w-full custom-scrollbar">
                                <table className="w-full text-xs table-auto border-collapse min-w-max">
                                    <thead className="bg-slate-50/50 text-slate-400 uppercase text-[9px] font-black border-b tracking-wider">
                                        <tr>
                                            {columnasSeleccionadas.map(colKey => {
                                                let defCampo;
                                                Object.values(camposDisponibles).forEach(seccion => {
                                                    const match = seccion.find(c => c.key === colKey);
                                                    if (match) defCampo = match;
                                                });
                                                const labelCabecera = defCampo ? defCampo.label : colKey;
                                                const isNumeric = MONETARY_KEYS.includes(colKey);

                                                return (
                                                    <th 
                                                        key={colKey} 
                                                        className={`p-3 px-5 font-black text-slate-500 select-none truncate ${
                                                            isNumeric ? 'text-right' : 'text-left'
                                                        }`}
                                                    >
                                                        {labelCabecera}
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    </thead>
                                    <tbody className="font-semibold text-slate-700 divide-y divide-slate-100 bg-white">
                                        {grupo.items.slice(0, 20).map((row, idx) => (
                                            <tr key={row.id || idx} className="hover:bg-slate-50/30 transition-colors duration-150">
                                                {columnasSeleccionadas.map(colKey => {
                                                    const isNumeric = MONETARY_KEYS.includes(colKey);
                                                    return (
                                                        <td 
                                                            key={colKey} 
                                                            className={`p-3 px-5 truncate ${isNumeric ? 'text-right' : 'text-left'}`} 
                                                            title={row[colKey] !== undefined ? String(row[colKey]) : ''}
                                                        >
                                                            {formatCellValue(colKey, row[colKey], row)}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}

                                        {grupo.items.length > 20 && (
                                            <tr>
                                                <td 
                                                    colSpan={columnasSeleccionadas.length || 1} 
                                                    className="p-3 bg-slate-50/50 text-slate-400 italic text-[11px] font-bold text-center border-t border-slate-100"
                                                >
                                                    Y {grupo.items.length - 20} registros adicionales en este grupo. (El archivo Excel exportará el 100% de la información agrupada)
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                );
            })}

            <style>{`
                .custom-scrollbar::-webkit-scrollbar { height: 8px; width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                .animate-fade-in { animation: fadeIn 0.2s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

const mostrarAcumulados = (grupo) => {
    return grupo.totalPrima > 0 || grupo.totalImporte > 0;
};

export default GroupedPreview;